import React, { useEffect } from 'react';
import { App, Button, Card, Result, Spin, Typography, Space } from 'antd';
import { useNavigate, useSearchParams } from 'react-router';
import { useLanguage } from 'src/providers/LanguageProvider';
import api from 'src/services/api';
import { CheckCircleTwoTone, CloseCircleTwoTone } from '@ant-design/icons';

const { Paragraph } = Typography;

const pageWrapStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  background:
    'linear-gradient(180deg, rgba(247,249,252,1) 0%, rgba(243,246,250,1) 50%, rgba(239,244,250,1) 100%)',
};

const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 520,
  borderRadius: 12,
  boxShadow:
    '0 4px 10px rgba(0,0,0,0.06), 0 12px 24px rgba(0,0,0,0.04)',
  overflow: 'hidden',
};

const contentWrapStyle: React.CSSProperties = {
  padding: 24,
};

const centerBoxStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '60vh',
  padding: 24,
};

const VerifyAccountPage: React.FC = () => {
  const [loading, setLoading] = React.useState(false);
  const [resending, setResending] = React.useState(false);
  const [status, setStatus] = React.useState<'idle' | 'success' | 'error'>('idle');
  const [errMessage, setErrMessage] = React.useState<string | null>(null);

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const navigate = useNavigate();
  const { t } = useLanguage();
  const { message } = App.useApp();

  // t güvenli sarmalayıcıları
  const safeT = (key: string) => (t ? String(t(key)) : key);
  const fmt = (key: string, params: Record<string, string | number>) => {
    let s = safeT(key);
    Object.entries(params).forEach(([k, v]) => {
      s = s.replace(new RegExp(`{{\\s*${k}\\s*}}`, 'g'), String(v));
    });
    return s;
  };

  // token yoksa login'e yönlendir
  useEffect(() => {
    if (!token) {
      navigate('/login');
    }
  }, [token, navigate]);

  const verify = async (tk: string) => {
    setLoading(true);
    try {
      await api.get(`/auth/verify/account?token=${tk}`);
      setStatus('success');
      // 2 sn sonra yönlendir
      setTimeout(() => navigate('/login'), 2000);
    } catch (e: any) {
      const err_code = e?.response?.data?.message || 'BACKEND_INTERNAL_ERROR';
      setErrMessage(err_code);
      setStatus('error');
      message.error(t ? String(t(err_code)) : err_code);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    verify(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleResend = async () => {
    if (!token) return;
    try {
      setResending(true);
      await api.post('/auth/verify/resend', { token }); // ihtiyacına göre endpoint/body’yi güncelle
      message.success(t ? String(t('VERIFICATION_EMAIL_RESENT')) : 'Verification email resent.');
    } catch (e: any) {
      const err_code = e?.response?.data?.message || 'BACKEND_INTERNAL_ERROR';
      message.error(t ? String(t(err_code)) : err_code);
    } finally {
      setResending(false);
    }
  };

  // Yükleniyor / idle ise ortalanmış spinner
  if (loading || status === 'idle') {
    return (
      <div style={pageWrapStyle}>
        <Card style={cardStyle} bodyStyle={{ padding: 0 }}>
          <div style={centerBoxStyle}>
            <Space direction="vertical" align="center" size="large">
              <Spin size="large" />
              <Paragraph style={{ margin: 0, color: 'rgba(0,0,0,0.45)' }}>
                {t ? String(t('PLEASE_WAIT')) : 'Please wait...'}
              </Paragraph>
            </Space>
          </div>
        </Card>
      </div>
    );
  }

  // Başarılı ekran
  if (status === 'success') {
    const seconds = 2;
    const subTitleText =
      t ? fmt('REDIRECTING_IN_SECONDS', { seconds }) : `You will be redirected in ${seconds} seconds...`;

    return (
      <div style={pageWrapStyle}>
        <Card style={cardStyle} bodyStyle={{ padding: 0 }}>
          <div style={contentWrapStyle}>
            <Result
              status="success"
              icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}
              title={t ? safeT('ACCOUNT_VERIFIED') : 'Account verified'}
              subTitle={subTitleText}
            />
          </div>
        </Card>
      </div>
    );
  }

  // Hata ekranı
  const showResend = errMessage === 'INVALID_OR_EXPIRED_TOKEN';

  return (
    <div style={pageWrapStyle}>
      <Card style={cardStyle} bodyStyle={{ padding: 0 }}>
        <div style={contentWrapStyle}>
          <Result
            status="error"
            icon={<CloseCircleTwoTone twoToneColor="#ff4d4f" />}
            title={t ? safeT('VERIFICATION_FAILED') : 'Verification failed'}
            subTitle={t ? safeT(errMessage || 'BACKEND_INTERNAL_ERROR') : (errMessage || 'Something went wrong.')}
            extra={
              <Space wrap>
                {showResend && (
                  <Button type="primary" onClick={handleResend} loading={resending}>
                    {t ? safeT('RESEND_VERIFICATION') : 'Resend verification'}
                  </Button>
                )}
                <Button onClick={() => navigate('/login')}>
                  {t ? safeT('GO_TO_LOGIN') : 'Go to Login'}
                </Button>
              </Space>
            }
          />
        </div>
      </Card>
    </div>
  );
};

export default VerifyAccountPage;
